import { useState, useEffect, useRef, useCallback } from "react";
import httpService from "../../services/http.service";
import { getCMSApiRoute } from "../api.utils";

// Cache version for invalidation when data structure changes
const CACHE_VERSION = "1.0";
const CACHE_PREFIX = "home_showcase_";

/**
 * Get cached data from localStorage
 * @param {string} key - The cache key
 * @returns {Object|null} Cached data or null
 */
const getCachedData = (key) => {
    try {
        const cached = localStorage.getItem(CACHE_PREFIX + key);
        if (!cached) return null;

        const parsed = JSON.parse(cached);

        // Validate cache version
        if (parsed.version !== CACHE_VERSION) {
            localStorage.removeItem(CACHE_PREFIX + key);
            return null;
        }

        return parsed.data;
    } catch (error) {
        console.warn(`[useHomeShowcaseData] Error reading cache for ${key}:`, error);
        return null;
    }
};

/**
 * Save data to localStorage cache
 * @param {string} key - The cache key
 * @param {Object} data - Data to cache
 */
const setCachedData = (key, data) => {
    try {
        const cacheEntry = {
            data,
            timestamp: Date.now(),
            version: CACHE_VERSION,
        };
        localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(cacheEntry));
    } catch (error) {
        console.warn(`[useHomeShowcaseData] Error writing cache for ${key}:`, error);
    }
};

/**
 * Check if user is logged in
 * @returns {boolean}
 */
const isUserLoggedIn = () => {
    try {
        const token = localStorage.getItem("webTvUserToken");
        return !!token && token.length > 0;
    } catch {
        return false;
    }
};

/**
 * Transform continue-watch items to flatten the nested content structure
 * API returns: { content: {...contentData}, elapsedTime, totalTime, updatedAt, options?, watchedEpisode? }
 * Cards expect: { ...contentData, elapsedTime, totalTime, seasonNumber, episodeNumber }
 * 
 * Season/episode can be in multiple locations (following Flutter logic):
 * - item['seasonNumber'] / item['episodeNumber']
 * - item['season'] / item['episode']
 * - content['seasonNumber'] / content['episodeNumber']
 * - options['seasonNumber'] / options['episodeNumber']
 * - watchedEpisode['seasonNumber'] / watchedEpisode['episodeNumber']
 * 
 * @param {Array} items - Continue watch items from API
 * @returns {Array} Transformed items with flattened structure
 */
const transformContinueWatch = (items) => {
    if (!Array.isArray(items)) return [];

    return items.map(item => {
        const content = item.content || {};
        const options = item.options || {};
        const watchedEpisode = content.watchedEpisode || item.watchedEpisode || {};

        // Extract season number from all possible locations
        const rawSeason = item.seasonNumber ?? item.season ??
            content.seasonNumber ?? content.season ??
            options.seasonNumber ?? options.season ??
            watchedEpisode.seasonNumber ?? watchedEpisode.season;

        // Extract episode number from all possible locations
        const rawEpisode = item.episodeNumber ?? item.episode ??
            content.episodeNumber ?? content.episode ??
            options.episodeNumber ?? options.episode ??
            watchedEpisode.episodeNumber ?? watchedEpisode.episode;

        // Parse to int if string
        const seasonNumber = typeof rawSeason === 'number' ? rawSeason :
            (typeof rawSeason === 'string' ? parseInt(rawSeason, 10) : null);
        const episodeNumber = typeof rawEpisode === 'number' ? rawEpisode :
            (typeof rawEpisode === 'string' ? parseInt(rawEpisode, 10) : null);

        // Flatten: merge content properties with watch progress metadata
        return {
            ...content,
            elapsedTime: item.elapsedTime || 0,
            totalTime: item.totalTime || 0,
            updatedAt: item.updatedAt,
            seasonNumber: isNaN(seasonNumber) ? null : seasonNumber,
            episodeNumber: isNaN(episodeNumber) ? null : episodeNumber,
        };
    });
};

/**
 * Extract results array from API response
 * Handles { data: { results: [...] } } structure
 * @param {Object} response - API response
 * @param {boolean} isContinueWatch - If true, transform the nested content structure
 * @returns {Array} Results array or empty array
 */
const extractResults = (response, isContinueWatch = false) => {
    console.log("[extractResults] response:", response, "isContinueWatch:", isContinueWatch);
    try {
        let results = [];

        if (response?.data?.data?.results) {
            results = response.data.data.results;
        } else if (response?.data?.results) {
            results = response.data.results;
        }

        // Transform continue-watch data to flatten nested content
        if (isContinueWatch && results.length > 0) {
            console.log("[extractResults] Transforming continue-watch data");
            results = transformContinueWatch(results);
        }

        console.log("[extractResults] Extracted:", results.length, "items");
        return results;
    } catch {
        return [];
    }
};

/**
 * Custom hook for fetching home showcase private data
 * (Continue Watch, You May Like) and public data (Recently Updated)
 * 
 * Uses stale-while-revalidate pattern similar to useShowcaseCache
 * 
 * @returns {Object} { continueWatch, youMayLike, recentlyUpdated, loading, isLoggedIn }
 */
export function useHomeShowcaseData() {
    console.log("[useHomeShowcaseData] ===== HOOK FUNCTION CALLED =====");
    const [continueWatch, setContinueWatch] = useState(null);
    const [youMayLike, setYouMayLike] = useState(null);
    const [recentlyUpdated, setRecentlyUpdated] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    const initRef = useRef(false);
    const isMountedRef = useRef(true);

    // Fetch all data
    const fetchAllData = useCallback(async () => {
        console.log("[useHomeShowcaseData] fetchAllData START");
        const loggedIn = isUserLoggedIn();
        console.log("[useHomeShowcaseData] isLoggedIn:", loggedIn);
        setIsLoggedIn(loggedIn);

        const promises = [];
        const promiseKeys = [];

        // Recently Updated (public - always fetch)
        console.log("[useHomeShowcaseData] Fetching recently-updated...");
        promises.push(
            httpService.get(getCMSApiRoute("showcase/recently-updated"), {
                params: { pageIndex: 15, pageNumber: 1 }
            }).catch((err) => {
                console.error("[useHomeShowcaseData] recently-updated error:", err);
                return null;
            })
        );
        promiseKeys.push("recentlyUpdated");

        // Private data (only if logged in)
        if (loggedIn) {
            console.log("[useHomeShowcaseData] Fetching continue-watch...");
            promises.push(
                httpService.get(getCMSApiRoute("showcase/continue-watch")).catch((err) => {
                    console.error("[useHomeShowcaseData] continue-watch error:", err);
                    return null;
                })
            );
            promiseKeys.push("continueWatch");

            console.log("[useHomeShowcaseData] Fetching user-may-like...");
            promises.push(
                httpService.get(getCMSApiRoute("showcase/user-may-like")).catch((err) => {
                    console.error("[useHomeShowcaseData] user-may-like error:", err);
                    return null;
                })
            );
            promiseKeys.push("youMayLike");
        }

        try {
            console.log("[useHomeShowcaseData] Awaiting Promise.all...");
            const results = await Promise.all(promises);
            console.log("[useHomeShowcaseData] Promise.all DONE, results:", results);

            // Note: We don't bail out on unmount anymore because React StrictMode
            // remounts components but preserves state. The data should still be processed.

            const data = {};
            promiseKeys.forEach((key, index) => {
                console.log("[useHomeShowcaseData] Extracting", key, "from result:", results[index]);
                // Pass isContinueWatch=true for continue-watch to transform nested content structure
                const isContinueWatch = key === "continueWatch";
                data[key] = extractResults(results[index], isContinueWatch);
                console.log("[useHomeShowcaseData] Extracted", key, ":", data[key]);
            });

            console.log("[useHomeShowcaseData] fetchAllData DONE, data:", data);
            return data;
        } catch (error) {
            console.error("[useHomeShowcaseData] Fetch error:", error);
            return {};
        }
    }, []);

    // Main initialization effect
    useEffect(() => {
        console.log("[useHomeShowcaseData] useEffect triggered, initRef:", initRef.current);
        if (initRef.current) {
            console.log("[useHomeShowcaseData] Already initialized, skipping");
            return;
        }
        initRef.current = true;
        isMountedRef.current = true;
        console.log("[useHomeShowcaseData] Starting initialization...");

        const initialize = async () => {
            // Check for cached data first
            const cachedRecentlyUpdated = getCachedData("recentlyUpdated");
            const loggedIn = isUserLoggedIn();
            const cachedContinueWatch = loggedIn ? getCachedData("continueWatch") : null;
            const cachedYouMayLike = loggedIn ? getCachedData("youMayLike") : null;

            const hasCachedData = cachedRecentlyUpdated || cachedContinueWatch || cachedYouMayLike;

            if (hasCachedData) {
                // Cache HIT: Show cached data immediately
                console.log("[useHomeShowcaseData] Cache HIT");

                if (cachedRecentlyUpdated) setRecentlyUpdated(cachedRecentlyUpdated);
                if (cachedContinueWatch) setContinueWatch(cachedContinueWatch);
                if (cachedYouMayLike) setYouMayLike(cachedYouMayLike);

                setLoading(false);

                // Background refetch
                const freshData = await fetchAllData();

                // Note: Don't bail on unmount - React StrictMode remounts preserve state
                console.log("[useHomeShowcaseData] Cache HIT - Updating with fresh data:", freshData);

                if (freshData.recentlyUpdated?.length > 0) {
                    console.log("[useHomeShowcaseData] Setting recentlyUpdated:", freshData.recentlyUpdated.length, "items");
                    setRecentlyUpdated(freshData.recentlyUpdated);
                    setCachedData("recentlyUpdated", freshData.recentlyUpdated);
                }
                if (freshData.continueWatch?.length > 0) {
                    console.log("[useHomeShowcaseData] Setting continueWatch:", freshData.continueWatch.length, "items");
                    setContinueWatch(freshData.continueWatch);
                    setCachedData("continueWatch", freshData.continueWatch);
                }
                if (freshData.youMayLike?.length > 0) {
                    console.log("[useHomeShowcaseData] Setting youMayLike:", freshData.youMayLike.length, "items");
                    setYouMayLike(freshData.youMayLike);
                    setCachedData("youMayLike", freshData.youMayLike);
                }
            } else {
                // Cache MISS: Fetch fresh data
                console.log("[useHomeShowcaseData] Cache MISS");
                setLoading(true);

                const freshData = await fetchAllData();

                // Note: Don't bail on unmount - React StrictMode remounts preserve state
                console.log("[useHomeShowcaseData] Cache MISS - Setting fresh data:", freshData);

                if (freshData.recentlyUpdated?.length > 0) {
                    console.log("[useHomeShowcaseData] Setting recentlyUpdated:", freshData.recentlyUpdated.length, "items");
                    setRecentlyUpdated(freshData.recentlyUpdated);
                    setCachedData("recentlyUpdated", freshData.recentlyUpdated);
                }
                if (freshData.continueWatch?.length > 0) {
                    console.log("[useHomeShowcaseData] Setting continueWatch:", freshData.continueWatch.length, "items");
                    setContinueWatch(freshData.continueWatch);
                    setCachedData("continueWatch", freshData.continueWatch);
                }
                if (freshData.youMayLike?.length > 0) {
                    console.log("[useHomeShowcaseData] Setting youMayLike:", freshData.youMayLike.length, "items");
                    setYouMayLike(freshData.youMayLike);
                    setCachedData("youMayLike", freshData.youMayLike);
                }

                setLoading(false);
                console.log("[useHomeShowcaseData] Cache MISS - Done, loading set to false");
            }
        };

        initialize();

        return () => {
            isMountedRef.current = false;
        };
    }, [fetchAllData]);

    return {
        continueWatch,
        youMayLike,
        recentlyUpdated,
        loading,
        isLoggedIn,
    };
}

/**
 * Clear all home showcase cache (call on logout)
 */
export function clearHomeShowcaseCache() {
    try {
        localStorage.removeItem(CACHE_PREFIX + "recentlyUpdated");
        localStorage.removeItem(CACHE_PREFIX + "continueWatch");
        localStorage.removeItem(CACHE_PREFIX + "youMayLike");
    } catch (error) {
        console.warn("[useHomeShowcaseData] Error clearing cache:", error);
    }
}

/**
 * Refetch continue-watch data and update cache
 * Call this when player exits to refresh the continue-watch row
 * @returns {Promise<Array>} Fresh continue-watch items
 */
export async function refetchContinueWatch() {
    try {
        // Check if user is logged in
        const token = localStorage.getItem("webTvUserToken");
        if (!token || token.length === 0) {
            console.log("[refetchContinueWatch] User not logged in, skipping");
            return [];
        }

        console.log("[refetchContinueWatch] Fetching fresh continue-watch data...");
        const response = await httpService.get(getCMSApiRoute("showcase/continue-watch"));

        let results = [];
        if (response?.data?.data?.results) {
            results = response.data.data.results;
        } else if (response?.data?.results) {
            results = response.data.results;
        }

        // Transform the nested content structure
        if (results.length > 0) {
            results = transformContinueWatch(results);
        }

        // Update cache
        setCachedData("continueWatch", results);
        console.log("[refetchContinueWatch] Updated cache with", results.length, "items");

        return results;
    } catch (error) {
        console.warn("[refetchContinueWatch] Error:", error);
        return [];
    }
}

export default useHomeShowcaseData;

